
export interface LightMarkdownOptions {
    /**
     * Wenn true, vorhandenes HTML (außerhalb von Code/Pre) grob erlauben
     * und nur auf eine Allowlist reduzieren; andernfalls wird HTML escaped.
     * Default: false (sicherer).
     */
    allowExistingHtml?: boolean;

    /**
     * Erlaubte Tags bei allowExistingHtml=true (Attribute werden entfernt).
     * Beispiel: ['b','i','em','strong','u','a','br'].
     */
    allowedTags?: string[];

    /**
     * Welche Tags für Fett/Kursiv verwendet werden sollen (Default: b/i,
     * um die Vorlage zu spiegeln).
     */
    tagStyle?: {
        boldTag?: 'b' | 'strong';
        italicTag?: 'i' | 'em';
    };

    /**
     * Ob Ergebnis in <p>…</p> gewrappt wird (wie im Beispiel der Vorlage).
     * Default: true.
     */
    wrapInParagraph?: boolean;
}

type Token = { key: string; html: string };

export class LightMarkdownConverter {
    private readonly allowExistingHtml: boolean;
    private readonly allowedTags: string[];
    private readonly boldTag: 'b' | 'strong';
    private readonly italicTag: 'i' | 'em';
    private readonly wrapInParagraph: boolean;

    private readonly TOKEN_PREFIX = '[[--LM-TOKEN-';
    private readonly TOKEN_SUFFIX = '--]]';

    constructor(options: LightMarkdownOptions = {}) {
        this.allowExistingHtml = options.allowExistingHtml ?? true;
        this.allowedTags = options.allowedTags ?? ['b','br','i','em','strong','u','a'];
        this.boldTag = options.tagStyle?.boldTag ?? 'b';
        this.italicTag = options.tagStyle?.italicTag ?? 'i';
        this.wrapInParagraph = options.wrapInParagraph ?? false;
    }

    /**
     * Entspricht der Vorlage-Funktion light-markdown.toHtml(text).
     * Konvertiert vereinfachtes Markup in HTML. Schützt Codebereiche
     * vor weiterer Formatierung und kümmert sich optional um HTML-Sicherheit.
     */
    public toHtml(input: string): string {
        if (!input) return this.wrap('');
        let text = input;

        // --- 1) Code-/Pre-Bereiche tokenisieren ---
        const tokens: Token[] = [];

        const pushToken = (html: string): string => {
            const key =
                this.TOKEN_PREFIX +
                tokens.length +
                '_' +
                Math.random().toString(36).slice(2) +
                this.TOKEN_SUFFIX;
            tokens.push({ key, html });
            return key;
        };


        // a) Codeblock: ```...```
        text = text.replace(/```([\s\S]*?)```/g, (_m, inner) => {
            const trimmed = inner.replace(/^\s*\n/, '').replace(/\s*$/, '');
            const escaped = this.escapeHtml(trimmed);
            return pushToken('<pre><code>' + escaped + '</code></pre>');
        });

        // b) Inline-Code: `...`
        text = text.replace(/`([^`\n]+)`/g, (_m, inner) => {
            const escaped = this.escapeHtml(inner);
            return pushToken('<code>' + escaped + '</code>');
        });

        // --- 2) Restlichen Text absichern ---
        let safe = this.allowExistingHtml
            ? this.sanitizeAllowlist(text, this.allowedTags)
            : this.escapeHtml(text);

        // --- 3) Inline-Formatierungen anwenden ---
        const boldTag = this.boldTag;
        const italicTag = this.italicTag;

        // Fett: *bold*
        safe = safe.replace(
            /\*(?!\s)([^*\n]+?)(?<!\s)\*/g,
            (_m, inner) => '<' + boldTag + '>' + inner + '</' + boldTag + '>'
        );

        // Kursiv: _italic_
        safe = safe.replace(
            /_(?!\s)([^_\n]+?)(?<!\s)_/g,
            (_m, inner) => '<' + italicTag + '>' + inner + '</' + italicTag + '>'
        );

        // --- 4) Tokens wieder einsetzen ---
        for (const t of tokens) {
            safe = safe.replace(t.key, t.html);
        }

        // --- 5) Optional <p>‑Wrapper ---
        return this.wrap(safe);
    }

    // ---------- Hilfsfunktionen ----------

    private wrap(html: string): string {
        if (!this.wrapInParagraph) return html;
        return '<p>' + html + '</p>';
    }

    private escapeHtml(s: string): string {
        return s
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    private sanitizeAllowlist(html: string, allowed: string[] = []): string {
        let out = html;

        // <script>/<style> vollständig entfernen
        out = out.replace(/<\s*(script|style)\b[\s\S]*?<\s*\/\s*\1\s*>/gi, '');

        // Tags filtern, Attribute strippen
        out = out.replace(/<\s*\/?\s*([a-zA-Z0-9:-]+)([^>]*)>/g, (m, tag) => {
            const t = String(tag).toLowerCase();
            const isClose = m.startsWith('</');
            const isSelfClosing = /\/\s*>$/.test(m);

            if (allowed.indexOf(t) === -1) {
                return '';
            }
            if (isClose) return '</' + t + '>';
            return isSelfClosing ? '<' + t + ' />' : '<' + t + '>';
        });

        // on*-Handler und javascript:-URLs grob entfernen
        out = out.replace(/\son[a-z]+\s*=\s*(['"]).*?\1/gi, '');
        out = out.replace(/\s(href|src)\s*=\s*(['"])\s*javascript:[^'"]*\2/gi, '');

        return out;
    }
}
